/**
 * Percentile utilities for slate-relative scoring.
 *
 * Used by the optimizer to assign tier labels based on where a parlay falls
 * within the current slate, rather than against fixed score thresholds.
 * On a weak slate, a "strong" parlay by absolute score may actually be
 * mediocre — relative tiers reflect what's actually best available today.
 */

/**
 * Compute the percentile rank of a value within an array of values.
 *
 * Returns a number in [0, 1] where 1.0 means "highest in the set".
 * Uses linear interpolation between adjacent values when the target falls
 * between two known data points. Empty arrays return 0.
 *
 * @param value - The value whose rank to compute
 * @param values - The reference distribution (will be sorted ascending internally)
 */
export function percentileRank(value: number, values: number[]): number {
  if (values.length === 0) return 0;
  if (values.length === 1) return value >= values[0] ? 1 : 0;

  const sorted = [...values].sort((a, b) => a - b);

  // Max check first so that an all-equal distribution ranks as 1 (top-of-set)
  // rather than 0 — useful for "am I in the top X%" tier assignment semantics.
  if (value >= sorted[sorted.length - 1]) return 1;
  if (value <= sorted[0]) return 0;

  // Find the position via binary search
  let lo = 0;
  let hi = sorted.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (sorted[mid] < value) lo = mid + 1;
    else hi = mid;
  }

  // sorted[lo] is the first element >= value
  // Compute fractional rank using linear interpolation between adjacent points
  const lowerIdx = lo - 1;
  const upperIdx = lo;
  const lower = sorted[lowerIdx];
  const upper = sorted[upperIdx];

  if (lower === upper) return upperIdx / (sorted.length - 1);

  const fraction = (value - lower) / (upper - lower);
  const rank = (lowerIdx + fraction) / (sorted.length - 1);
  return rank;
}

/**
 * Return the value at a given percentile (0-1) within an array.
 *
 * Uses linear interpolation between adjacent values. p=0 returns the minimum,
 * p=1 returns the maximum. Empty arrays return 0.
 *
 * @param values - The distribution to sample
 * @param p - Percentile in [0, 1]
 */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  if (values.length === 1) return values[0];
  if (p <= 0) return Math.min(...values);
  if (p >= 1) return Math.max(...values);

  const sorted = [...values].sort((a, b) => a - b);
  const position = p * (sorted.length - 1);
  const lowerIdx = Math.floor(position);
  const upperIdx = Math.ceil(position);

  if (lowerIdx === upperIdx) return sorted[lowerIdx];

  const fraction = position - lowerIdx;
  return sorted[lowerIdx] + fraction * (sorted[upperIdx] - sorted[lowerIdx]);
}
